import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
  Unsubscribe
} from 'firebase/firestore';
import { db } from './firebase';
import { CustomerMessage, OutboundEmail, MessageStatus, EmailSettings } from '../types';
import {
  sendContactAcknowledgementEmail,
  sendVolunteerReplyEmail,
  dispatchVolunteerEventNotification
} from './emailService';
import { DEFAULT_EMAIL_SETTINGS } from './emailTemplates';
import { generateReferenceNumber } from '../utils/referenceGenerator';
import { sanitizeFirestoreData } from '../utils/sanitizeFirestore';
import { generateReplyToken, hashReplyToken } from '../utils/tokenAuth';

const COLLECTION_NAME = 'customer_messages';

export function subscribeCustomerMessages(
  onData: (messages: CustomerMessage[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const messagesRef = collection(db, COLLECTION_NAME);
  return onSnapshot(
    messagesRef,
    (snapshot) => {
      const messages: CustomerMessage[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const replies: OutboundEmail[] = data.replies || (data.thread ? data.thread.map((t: any) => ({
          id: t.id || `reply-${Date.now()}`,
          sentAt: t.createdAt || new Date().toISOString(),
          volunteerName: t.senderName || 'Volunteer',
          toEmail: data.email || '',
          subject: data.subject || 'Enquiry Reply',
          body: t.message,
          isInternalNote: t.isInternalNote || false
        })) : []);

        const refNum = data.referenceNumber || `UL-MSG-${docSnap.id.substring(0, 6).toUpperCase()}`;

        return {
          id: docSnap.id,
          referenceNumber: refNum,
          customerName: data.customerName || 'Customer',
          email: data.email || '',
          phone: data.phone || '',
          subject: data.subject || 'Enquiry',
          message: data.message || '',
          createdAt: data.createdAt || new Date().toISOString(),
          status: (data.status === 'Awaiting Customer Reply' ? 'Awaiting Response' : data.status) || 'New',
          assignedVolunteer: data.assignedVolunteer || '',
          replies
        };
      });
      messages.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      onData(messages);
    },
    (err) => {
      console.error('Error listening to customer messages collection:', err);
      if (onError) onError(err);
    }
  );
}

export async function createCustomerMessage(
  data: Omit<CustomerMessage, 'id' | 'createdAt' | 'status'>,
  emailSettings?: EmailSettings
): Promise<CustomerMessage> {
  const newRef = doc(collection(db, COLLECTION_NAME));
  const refNum = generateReferenceNumber('UL-MSG');

  const newMessage: CustomerMessage = {
    ...data,
    id: newRef.id,
    referenceNumber: refNum,
    createdAt: new Date().toISOString(),
    status: 'New',
    replies: []
  };

  await setDoc(newRef, sanitizeFirestoreData(newMessage));

  // Trigger automatic email acknowledgement
  try {
    await sendContactAcknowledgementEmail(
      newMessage.customerName,
      newMessage.email,
      refNum,
      newMessage.message,
      newMessage.id,
      emailSettings || DEFAULT_EMAIL_SETTINGS
    );

    // Dispatch notification to active subscribed volunteers with Customer Messages access
    dispatchVolunteerEventNotification({
      eventType: 'new_message',
      customerMessage: newMessage,
      settings: emailSettings || DEFAULT_EMAIL_SETTINGS
    }).catch((notifErr) => {
      console.warn('Volunteer message notification dispatch failed:', notifErr);
    });
  } catch (emailErr) {
    console.error('Failed to dispatch contact acknowledgement email:', emailErr);
  }

  return newMessage;
}

export async function updateCustomerMessageStatus(
  id: string,
  status: MessageStatus
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, { status });
}

export async function assignCustomerMessage(
  id: string,
  assignedVolunteer: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, { assignedVolunteer });
}

export async function deleteCustomerMessage(
  id: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  await deleteDoc(docRef);
}

export async function addMessageReplyOrNote(
  id: string,
  customerMessage: CustomerMessage,
  volunteerName: string,
  replyBody: string,
  isInternalNote: boolean = false,
  customStatus?: MessageStatus,
  emailSettings?: EmailSettings
): Promise<OutboundEmail> {
  const docRef = doc(db, COLLECTION_NAME, id);
  const refNum = customerMessage.referenceNumber || `UL-MSG-${id.substring(0, 6).toUpperCase()}`;

  const formattedSubject = customerMessage.subject.includes(refNum)
    ? (customerMessage.subject.startsWith('Re:') ? customerMessage.subject : `Re: ${customerMessage.subject}`)
    : `Re: Uniform Loop Enquiry ${refNum}`;

  const newEmailRecord: OutboundEmail = {
    id: `reply-${Date.now()}`,
    sentAt: new Date().toISOString(),
    volunteerName,
    toEmail: customerMessage.email,
    subject: formattedSubject,
    body: replyBody,
    isInternalNote
  };

  // If not internal note, manage secure reply token and trigger external email service dispatch
  let newReplyTokenHash: string | undefined;
  let newReplyTokenExpiresAt: number | undefined;
  let updatedReplyTokens: { hash: string; expiresAt: number }[] | undefined;
  let updatedReplyTokenHashes: string[] | undefined;

  if (!isInternalNote) {
    try {
      const baseUrl = typeof window !== 'undefined' && window.location.origin
        ? window.location.origin
        : 'https://together-uniforms.pages.dev';

      // 1. Generate fresh 256-bit raw token for this volunteer reply
      const rawToken = generateReplyToken();
      newReplyTokenHash = await hashReplyToken(rawToken);
      newReplyTokenExpiresAt = Date.now() + 30 * 24 * 60 * 60 * 1000;
      const replyUrl = `${baseUrl}/reply/${rawToken}`;

      // 2. Assemble active token collection: keep unexpired older tokens, prune expired
      const now = Date.now();
      const activeTokens: { hash: string; expiresAt: number }[] = [];

      // Preserve existing unexpired tokens from replyTokens array
      if (Array.isArray(customerMessage.replyTokens)) {
        for (const t of customerMessage.replyTokens) {
          if (t && t.hash && typeof t.expiresAt === 'number' && t.expiresAt > now) {
            if (!activeTokens.some((et) => et.hash === t.hash)) {
              activeTokens.push({ hash: t.hash, expiresAt: t.expiresAt });
            }
          }
        }
      }

      // Preserve legacy unexpired single token if present
      if (
        customerMessage.replyTokenHash &&
        typeof customerMessage.replyTokenExpiresAt === 'number' &&
        customerMessage.replyTokenExpiresAt > now
      ) {
        if (!activeTokens.some((et) => et.hash === customerMessage.replyTokenHash)) {
          activeTokens.push({
            hash: customerMessage.replyTokenHash,
            expiresAt: customerMessage.replyTokenExpiresAt
          });
        }
      }

      // Append new token with its independent 30-day expiration
      activeTokens.push({ hash: newReplyTokenHash, expiresAt: newReplyTokenExpiresAt });
      updatedReplyTokens = activeTokens;
      updatedReplyTokenHashes = activeTokens.map((t) => t.hash);

      // 3. Dispatch volunteer reply email with working Reply to Uniform Loop button
      await sendVolunteerReplyEmail(
        customerMessage.customerName,
        customerMessage.email,
        volunteerName,
        id,
        replyBody,
        formattedSubject,
        emailSettings || DEFAULT_EMAIL_SETTINGS,
        replyUrl,
        refNum
      );
    } catch (emailErr) {
      console.error('Failed to dispatch volunteer reply email:', emailErr);
    }
  }

  const existingReplies = customerMessage.replies || [];
  const updatedReplies = [...existingReplies, newEmailRecord];
  const nextStatus: MessageStatus = customStatus || (isInternalNote ? customerMessage.status : 'In Progress');

  const updatePayload: Record<string, any> = {
    replies: updatedReplies,
    status: nextStatus,
    updatedAt: new Date().toISOString()
  };

  if (!isInternalNote) {
    if (updatedReplyTokens && updatedReplyTokenHashes) {
      updatePayload.replyTokens = updatedReplyTokens;
      updatePayload.replyTokenHashes = updatedReplyTokenHashes;
    }
    if (newReplyTokenHash && newReplyTokenExpiresAt) {
      // Legacy backwards-compatible fields reflect the latest active token
      updatePayload.replyTokenHash = newReplyTokenHash;
      updatePayload.replyTokenExpiresAt = newReplyTokenExpiresAt;
    }
  }

  await updateDoc(docRef, sanitizeFirestoreData(updatePayload));

  return newEmailRecord;
}
